import { useNavigate } from 'react-router-dom';
import { User, FileText, Gavel, FileDown, LogOut, CreditCard } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useSubscription } from '@/lib/billing-query';

export function ClientDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: sub } = useSubscription();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const navCards = [
    {
      icon: User,
      label: 'My Profile',
      desc: 'View your personal information and account details.',
      to: '/me',
      enabled: true,
    },
    {
      icon: FileText,
      label: 'Credit Reports',
      desc: 'Review your credit reports and track changes over time.',
      to: '/me/reports',
      enabled: false, // will be implemented in the portal controller
    },
    {
      icon: Gavel,
      label: 'Disputes',
      desc: 'Track the status of your dispute rounds.',
      to: '/me/disputes',
      enabled: false,
    },
    {
      icon: FileDown,
      label: 'My Letters',
      desc: 'Download dispute letters sent to credit bureaus.',
      to: '/me/letters',
      enabled: false,
    },
    {
      icon: CreditCard,
      label: 'Billing',
      desc: 'View your agency subscription and plan.',
      to: '/billing',
      enabled: false,
    },
  ];

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <User className="size-4" />
            </div>
            <span className="font-semibold">RepairPulse</span>
            <Badge variant="muted" className="ml-2 uppercase text-xs">
              Client Portal
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              {user?.firstName ?? ''} {user?.lastName ?? ''}
            </span>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="size-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome, {user?.firstName ?? user?.email}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            This is your personal portal. Track your credit repair progress
            and manage your disputes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {navCards.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.to}
                className={`rounded-lg border p-5 ${
                  item.enabled ? 'bg-card cursor-pointer hover:border-primary/50 transition-colors' : 'bg-muted/40'
                }`}
                onClick={() => item.enabled && navigate(item.to)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="size-5 text-primary" />
                  <h3 className="font-medium">{item.label}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {item.desc}
                </p>
                {item.enabled ? (
                  <span className="text-sm text-primary">Open →</span>
                ) : (
                  <span className="text-xs text-muted-foreground">Coming soon</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick status card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Account Status</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Agency</span>
              <span>{sub ? `Plan: ${sub.planTier}` : '—'}</span>
            </div>
          </CardContent>
        </Card>
      </section>
    </main>
  );
}
